import { Badge, Button, Chip, EmptyState, Spinner, StatusDot } from "@salesko/design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Link2, Monitor } from "lucide-react";
import { useState } from "react";
import type {
  PersonalAgentDevice,
  PersonalAgentPairingCodeResponse,
  PersonalAgentPresenceHint
} from "@salesko/contracts";
import { useI18n, type Dictionary } from "../../i18n";
import {
  createPersonalAgentPairingCode,
  listPersonalAgentDevices,
  listPersonalAgentPresenceHints,
  revokePersonalAgentDevice
} from "../../lib/api";
import { fetchLatestLocalAgentRelease } from "../../local-agent-release";
import { SettingsNote, SettingsSection } from "./SettingsSection";

/**
 * "Connect a computer" in three steps: issue a one-time pairing code, wait for
 * the daemon to check in, then manage the computers that did.
 *
 * The computer's identity is the daemon-reported `deviceName`; the web never
 * renames it. Runtime facts (`runtimes`, `localAgentRelease`, `authPresent`)
 * come from the presence hint and are rendered only when the daemon actually
 * reported them — a missing fact stays missing rather than becoming a default.
 * No credential of any kind is collected or displayed here: provider sign-in
 * lives in the daemon's own credential store on the user's machine.
 */
export function ComputersPanel({ enabled }: { enabled: boolean }) {
  const queryClient = useQueryClient();
  const [pairing, setPairing] = useState<PersonalAgentPairingCodeResponse | null>(null);

  const devicesQuery = useQuery({
    queryKey: ["personal-agent-devices"],
    queryFn: listPersonalAgentDevices,
    enabled,
    // While a pairing code is outstanding the daemon may check in at any
    // moment, so step 2 polls instead of leaving the user to refresh.
    refetchInterval: (query) => (pairing && !(query.state.data?.devices.some((device) => !device.revoked) ?? false) ? 5_000 : false),
    retry: 1,
    staleTime: 15_000
  });
  const presenceQuery = useQuery({
    queryKey: ["personal-agent-presence-hints"],
    queryFn: listPersonalAgentPresenceHints,
    enabled,
    retry: 1,
    staleTime: 15_000
  });
  const latestReleaseQuery = useQuery({
    queryKey: ["latest-local-agent-release"],
    queryFn: () => fetchLatestLocalAgentRelease(),
    enabled,
    retry: 1,
    staleTime: 60_000
  });

  const pairingMutation = useMutation({
    mutationFn: createPersonalAgentPairingCode,
    onSuccess: (response) => setPairing(response)
  });
  const revokeMutation = useMutation({
    mutationFn: revokePersonalAgentDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personal-agent-devices"] });
      queryClient.invalidateQueries({ queryKey: ["personal-agent-presence-hints"] });
    }
  });

  return (
    <ComputersPanelView
      devices={devicesQuery.data?.devices ?? []}
      devicesLoading={devicesQuery.isFetching}
      enabled={enabled}
      hasError={
        pairingMutation.isError || revokeMutation.isError || devicesQuery.isError || presenceQuery.isError
      }
      onCreatePairingCode={() => pairingMutation.mutate()}
      onRevoke={(deviceId) => revokeMutation.mutate(deviceId)}
      latestReleaseVersion={
        latestReleaseQuery.isPending ? undefined : (latestReleaseQuery.data?.version ?? null)
      }
      pairing={pairing}
      pairingPending={pairingMutation.isPending}
      presenceHints={presenceQuery.data?.presenceHints ?? []}
      revokePending={revokeMutation.isPending}
    />
  );
}

/**
 * The one and only install. `/install.sh` is served by this app's own Worker
 * (`src/install-script.ts`); it resolves the macOS binary for the machine's
 * architecture from the downloads bucket and verifies its checksum. The panel
 * names no version and no asset, so a release never redeploys this app.
 */
const INSTALL_COMMAND = "curl -fsSL https://salesko.ai/install.sh | sh -s --";

/** The public long-form version of these steps, for anyone who wants the whole picture first. */
const INSTALL_GUIDE_URL = "https://salesko.ai/install";

/** What `keysStayOnComputer` points at: the command that reads them, on their machine. */
const KEYS_LIST_COMMAND = "salesko-agent keys list";

export type ComputersPanelViewProps = {
  devices: PersonalAgentDevice[];
  devicesLoading: boolean;
  enabled: boolean;
  hasError: boolean;
  latestReleaseVersion: string | null | undefined;
  onCreatePairingCode: () => void;
  onRevoke: (deviceId: string) => void;
  pairing: PersonalAgentPairingCodeResponse | null;
  pairingPending: boolean;
  presenceHints: PersonalAgentPresenceHint[];
  revokePending: boolean;
};

/** Presentation only, so every step and card state can be rendered directly in tests. */
export function ComputersPanelView({
  devices,
  devicesLoading,
  enabled,
  hasError,
  latestReleaseVersion,
  onCreatePairingCode,
  onRevoke,
  pairing,
  pairingPending,
  presenceHints,
  revokePending
}: ComputersPanelViewProps) {
  const { t } = useI18n();
  const presenceByDeviceId = new Map(presenceHints.map((hint) => [hint.deviceId, hint]));
  // A revoked computer is history, not an option: it is not rendered at all, so
  // this list only ever reads as "the computers you can actually use".
  const activeDevices = devices.filter((device) => !device.revoked);
  // Step 2 only exists between "code issued" and "a computer showed up".
  const waitingForDevice = Boolean(pairing) && activeDevices.length === 0;
  const busy = devicesLoading || pairingPending;

  return (
    <SettingsSection
      ariaLabel={t.computers.ariaLabel}
      id="settings-computers"
      title={t.computers.title}
      trailing={busy ? <Spinner size={16} /> : <Monitor size={15} />}
    >
      {!enabled ? <SettingsNote>{t.computers.signIn}</SettingsNote> : null}

      {enabled ? (
        <>
          <SettingsNote tone="body">{t.computers.description}</SettingsNote>

          <div style={{ display: "grid", gap: "var(--space-3)" }}>
            <strong style={{ fontSize: "var(--text-body-sm)", color: "var(--text-strong)" }}>
              {t.computers.stepInstallTitle}
            </strong>
            <SettingsNote>{t.computers.stepInstallBody}</SettingsNote>
            <SettingsNote>{t.computers.stepInstallPrereq}</SettingsNote>
            <a
              href={INSTALL_GUIDE_URL}
              rel="noreferrer"
              style={{
                fontSize: "var(--text-body-sm)",
                fontFamily: "var(--font-sans)",
                color: "var(--accent)"
              }}
              target="_blank"
            >
              {t.computers.stepInstallGuideLink}
            </a>
            {pairing ? (
              <>
                <CommandBlock
                  command={`${INSTALL_COMMAND} ${pairing.pairingCode}`}
                  copiedLabel={t.computers.copied}
                  copyLabel={t.computers.copy}
                />
                <SettingsNote>{t.computers.setupExisting}</SettingsNote>
                <CommandBlock
                  command={`salesko-agent setup ${pairing.pairingCode}`}
                  copiedLabel={t.computers.copied}
                  copyLabel={t.computers.copy}
                />
                <SettingsNote>{t.computers.pairingCodeExpiresAt(new Date(pairing.expiresAt).toLocaleString())}</SettingsNote>
              </>
            ) : (
              <div>
                <Button
                  disabled={pairingPending}
                  iconLeft={<Link2 size={15} />}
                  onClick={onCreatePairingCode}
                  size="sm"
                  variant="secondary"
                >
                  {t.computers.createPairingCode}
                </Button>
              </div>
            )}
          </div>

          {waitingForDevice ? (
            <div style={{ display: "grid", gap: "var(--space-2)" }}>
              <strong style={{ fontSize: "var(--text-body-sm)", color: "var(--text-strong)" }}>
                {t.computers.stepWaitingTitle}
              </strong>
              <Spinner label={t.computers.stepWaitingBody} size={16} />
            </div>
          ) : null}

          {activeDevices.length === 0 && !waitingForDevice && !devicesLoading ? (
            <EmptyState icon={<Monitor size={20} />} title={t.computers.emptyTitle}>
              {t.computers.empty}
            </EmptyState>
          ) : null}

          {activeDevices.length > 0 ? (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-3)" }}>
              {activeDevices.map((device) => (
                <li key={device.deviceId}>
                  <ComputerCard
                    device={device}
                    hint={presenceByDeviceId.get(device.deviceId)}
                    latestReleaseVersion={latestReleaseVersion}
                    onRevoke={() => onRevoke(device.deviceId)}
                    revokePending={revokePending}
                    t={t}
                  />
                </li>
              ))}
            </ul>
          ) : null}

          {activeDevices.length > 0 ? (
            <div style={{ display: "grid", gap: "var(--space-2)" }}>
              <strong style={{ fontSize: "var(--text-body-sm)", color: "var(--text-strong)" }}>
                {t.computers.updateTitle}
              </strong>
              <SettingsNote>{t.computers.updateBody}</SettingsNote>
              <CommandBlock
                command="curl -fsSL https://salesko.ai/install.sh | sh"
                copiedLabel={t.computers.copied}
                copyLabel={t.computers.copy}
              />
              <strong style={{ fontSize: "var(--text-body-sm)", color: "var(--text-strong)" }}>
                {t.computers.startupTitle}
              </strong>
              <SettingsNote>{t.computers.startupBody}</SettingsNote>
              <CommandBlock
                command="salesko-agent setup"
                copiedLabel={t.computers.copied}
                copyLabel={t.computers.copy}
              />
            </div>
          ) : null}

          <SettingsNote>{t.computers.presenceIsHint}</SettingsNote>

          <div style={{ display: "grid", gap: 6 }}>
            <SettingsNote>{t.computers.keysStayOnComputer}</SettingsNote>
            <code
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--text-micro)",
                color: "var(--text-secondary)"
              }}
            >
              {KEYS_LIST_COMMAND}
            </code>
          </div>

          {hasError ? (
            <p
              role="alert"
              style={{ margin: 0, color: "var(--red-600)", fontSize: "var(--text-body-sm)", fontFamily: "var(--font-sans)" }}
            >
              {t.computers.updateError}
            </p>
          ) : null}
        </>
      ) : null}
    </SettingsSection>
  );
}

/**
 * `online` vs `offline` is the only reading the web is allowed to derive from a
 * presence hint: an absent hint and an explicit `offline` level both mean the
 * computer is not reachable right now. A revoked computer is neither.
 */
export function computerStatus(device: PersonalAgentDevice, hint: PersonalAgentPresenceHint | undefined) {
  if (device.revoked) {
    return "revoked" as const;
  }
  if (!hint || hint.level === "offline") {
    return "offline" as const;
  }
  return "online" as const;
}

function ComputerCard({
  device,
  hint,
  latestReleaseVersion,
  onRevoke,
  revokePending,
  t
}: {
  device: PersonalAgentDevice;
  hint: PersonalAgentPresenceHint | undefined;
  latestReleaseVersion: string | null | undefined;
  onRevoke: () => void;
  revokePending: boolean;
  t: Dictionary;
}) {
  const status = computerStatus(device, hint);
  const runtimes = hint?.runtimes ?? [];

  return (
    <div
      style={{
        display: "grid",
        gap: "var(--space-3)",
        padding: "var(--space-4)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        background: status === "revoked" ? "var(--surface-sunken)" : "var(--surface-card)",
        opacity: status === "revoked" ? 0.62 : 1
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <StatusDot status={status} srLabel={statusLabel(status, t)} />
          <strong style={{ fontSize: "var(--text-body-md)", color: "var(--text-strong)" }}>{device.deviceName}</strong>
          {status === "revoked" ? <Badge tone="neutral">{t.computers.revoked}</Badge> : null}
        </span>
        {status !== "revoked" ? (
          <Button disabled={revokePending} onClick={onRevoke} size="sm" variant="ghost">
            {t.computers.revoke}
          </Button>
        ) : null}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <SettingsNote>{statusLabel(status, t)}</SettingsNote>
        {hint ? (
          <SettingsNote>{t.computers.presenceHint(hint.level, new Date(hint.expiresAt).toLocaleString())}</SettingsNote>
        ) : (
          <SettingsNote>{t.computers.noPresenceHint}</SettingsNote>
        )}
        <SettingsNote>
          {hint?.localAgentRelease
            ? t.computers.installedRelease(hint.localAgentRelease)
            : t.computers.installedReleaseUnavailable}
        </SettingsNote>
        <SettingsNote>
          {latestReleaseVersion === undefined
            ? t.computers.latestReleaseLoading
            : latestReleaseVersion === null
              ? t.computers.latestReleaseUnavailable
              : t.computers.latestRelease(latestReleaseVersion)}
        </SettingsNote>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <SettingsNote>{t.computers.runtimes}</SettingsNote>
        {runtimes.length === 0 ? (
          <SettingsNote>{t.computers.noRuntimeFacts}</SettingsNote>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {runtimes.map((runtime) => (
              <Chip dot={false} key={runtime.id} tone="neutral">
                {/* Version and sign-in are optional daemon facts: render each
                    only when reported, never a fabricated placeholder. */}
                {runtime.version ? `${runtime.id} ${runtime.version}` : runtime.id}
                {runtime.authPresent === undefined ? null : (
                  <span style={{ marginLeft: 6, color: "var(--text-muted)", fontWeight: "var(--fw-regular)" }}>
                    {runtime.authPresent ? t.computers.signedIn : t.computers.notSignedIn}
                  </span>
                )}
              </Chip>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function statusLabel(status: ReturnType<typeof computerStatus>, t: Dictionary) {
  if (status === "revoked") {
    return t.computers.revoked;
  }
  return status === "online" ? t.computers.online : t.computers.offline;
}

function CommandBlock({
  command,
  copiedLabel,
  copyLabel
}: {
  command: string;
  copiedLabel: string;
  copyLabel: string;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)"
      }}
    >
      <code
        style={{
          flex: 1,
          minWidth: 0,
          overflowWrap: "anywhere",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--text-body-sm)",
          color: "var(--text-strong)"
        }}
      >
        {command}
      </code>
      <Button
        iconLeft={copied ? <Check size={14} /> : <Copy size={14} />}
        onClick={() => {
          void navigator.clipboard?.writeText(command).then(() => setCopied(true));
        }}
        size="sm"
        variant="ghost"
      >
        {copied ? copiedLabel : copyLabel}
      </Button>
    </div>
  );
}
